import React, { useState, useEffect } from 'react';
import { leadsService } from '../services/api';
import { Lead } from '../types';
import Layout from '../components/Layout';
import LeadTable from '../components/LeadTable';
import LeadFilters from '../components/LeadFilters';
import LeadModal from '../components/LeadModal';
import DashboardStats from '../components/DashboardStats';
import Pagination from '../components/Pagination';
import WhatsAppModal from '../components/WhatsAppModal';
import QuickActions from '../components/QuickActions';
import KanbanBoard from '../components/KanbanBoard';
import KanbanFilters from '../components/KanbanFilters';
import BulkAssignModal from '../components/BulkAssignModal';
import SaleConfirmationModal from '../components/SaleConfirmationModal';

const Dashboard: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<{ consultor?: number; status?: string; telefone?: string }>({});
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeadForWhatsApp, setSelectedLeadForWhatsApp] = useState<Lead | null>(null);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [filteredKanbanLeads, setFilteredKanbanLeads] = useState<Lead[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [selectedLeadForSale, setSelectedLeadForSale] = useState<Lead | null>(null);
  const [isSaleConfirmationModalOpen, setIsSaleConfirmationModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  const fetchLeads = async () => {
    try {
      setLoading(true);
      // No modo Kanban, buscar mais leads para ter uma visão completa
      const limit = viewMode === 'kanban' ? 100 : 10;
      const page = viewMode === 'kanban' ? 1 : currentPage;
      
      const response = await leadsService.getLeads({
        ...filters,
        page,
        limit
      });
      setLeads(response.data || response); // Suporte a ambos os formatos
      setFilteredKanbanLeads(response.data || response);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (err) {
      setError('Erro ao carregar leads');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [filters, currentPage, viewMode]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStatusUpdate = async (leadId: number, newStatus: string) => {
    try {
      await leadsService.updateLeadStatus(leadId, newStatus);
      await fetchLeads();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    }
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
    fetchLeads();
  };

  const handleCompraUpdate = async (leadId: number, comprou: boolean) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    if (comprou || lead.comprou) {
      // Se está marcando como comprou OU se já está comprado (para editar), abre modal
      setSelectedLeadForSale(lead);
      setIsSaleConfirmationModalOpen(true);
    } else {
      // Se está desmarcando uma lead não comprada, atualiza diretamente
      try {
        await leadsService.updateLeadComprou(leadId, false);
        await fetchLeads();
      } catch (err) {
        console.error('Erro ao atualizar status de compra:', err);
      }
    }
  };

  const handleSaleConfirmationComplete = () => {
    setSelectedLeadForSale(null);
    setIsSaleConfirmationModalOpen(false);
    fetchLeads();
  };

  const handleWhatsAppOpen = (lead: Lead) => {
    setSelectedLeadForWhatsApp(lead);
    setIsWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = () => {
    setIsWhatsAppModalOpen(false);
    setSelectedLeadForWhatsApp(null);
  };

  const handleKanbanFilterChange = (filteredLeads: Lead[]) => {
    setFilteredKanbanLeads(filteredLeads);
  };

  const handleLeadSelect = (leadId: number, selected: boolean) => {
    if (selected) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedLeads(leads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleBulkAssign = () => {
    setIsBulkAssignModalOpen(true);
  };

  const handleBulkAssignComplete = () => {
    setSelectedLeads([]);
    fetchLeads();
  };


  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
          {/* Dashboard Stats */}
          <DashboardStats />

          <div className="flex justify-between items-center mb-6 mt-8">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {viewMode === 'list' ? 'Lista de Leads' : 'Kanban de Leads'}
              </h2>
              
              {/* Alternador de Visualização */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'list'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Visualização em lista"
                >
                  📋 Lista
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'kanban'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Visualização em kanban"
                >
                  🗂️ Kanban
                </button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  showBulkActions
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title="Ativar seleção múltipla"
              >
                {showBulkActions ? '✓ Seleção Múltipla' : '☐ Seleção Múltipla'}
              </button>
              
              {showBulkActions && selectedLeads.length > 0 && (
                <button
                  onClick={handleBulkAssign}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
                  title={`Atribuir ${selectedLeads.length} leads selecionadas`}
                >
                  👤 Atribuir ({selectedLeads.length})
                </button>
              )}
              
              <button
                onClick={handleCreateLead}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                title="Criar nova lead no sistema"
              >
                Nova Lead
              </button>
            </div>
          </div>

          <QuickActions onRefresh={fetchLeads} />
          
          {viewMode === 'list' ? (
            <LeadFilters onFilterChange={setFilters} />
          ) : (
            <KanbanFilters leads={leads} onFilterChange={handleKanbanFilterChange} />
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : viewMode === 'list' ? (
            <>
              <LeadTable
                leads={leads}
                onStatusUpdate={handleStatusUpdate}
                onEditLead={handleEditLead}
                onCompraUpdate={handleCompraUpdate}
                onWhatsAppOpen={handleWhatsAppOpen}
                selectedLeads={selectedLeads}
                onLeadSelect={handleLeadSelect}
                onSelectAll={handleSelectAll}
                showBulkActions={showBulkActions}
              />
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                total={pagination.total}
                limit={pagination.limit}
              />
            </>
          ) : (
            <div className="mt-6">
              <KanbanBoard
                leads={filteredKanbanLeads}
                onStatusUpdate={handleStatusUpdate}
                onEditLead={handleEditLead}
                onWhatsAppOpen={handleWhatsAppOpen}
                loading={loading}
              />
            </div>
          )}
        </div>

      {isModalOpen && (
        <LeadModal lead={selectedLead} onClose={handleCloseModal} />
      )}

      {isWhatsAppModalOpen && selectedLeadForWhatsApp && (
        <WhatsAppModal lead={selectedLeadForWhatsApp} onClose={handleCloseWhatsAppModal} />
      )}

      {isBulkAssignModalOpen && (
        <BulkAssignModal
          selectedLeads={leads.filter(lead => selectedLeads.includes(lead.id))}
          onClose={() => setIsBulkAssignModalOpen(false)}
          onAssignComplete={handleBulkAssignComplete}
        />
      )}

      {isSaleConfirmationModalOpen && selectedLeadForSale && (
        <SaleConfirmationModal
          lead={selectedLeadForSale}
          onClose={() => setIsSaleConfirmationModalOpen(false)}
          onConfirm={handleSaleConfirmationComplete}
        />
      )}
    </Layout>
  );
};

export default Dashboard;